import React, { useEffect, useMemo, useState } from 'react';
import './StreakOverlayScreen.css';

function RollingDigitColumn({ fromDigit, toDigit, fast, delayMs }) {
  const from = fromDigit === ' ' ? null : Number(fromDigit);
  const to = toDigit === ' ' ? null : Number(toDigit);

  if (from === null && to === null) {
    return <span className="streak-rolling-digit streak-rolling-digit--spacer" aria-hidden="true" />;
  }

  const start = Number.isFinite(from) ? from : 0;
  const end = Number.isFinite(to) ? to : start;
  const [offset, setOffset] = useState(start);

  useEffect(() => {
    setOffset(start);
    let timer;
    const frame = window.requestAnimationFrame(() => {
      timer = window.setTimeout(() => setOffset(end), delayMs);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      if (timer) window.clearTimeout(timer);
    };
  }, [start, end, delayMs]);

  return (
    <span className="streak-rolling-digit" aria-hidden="true">
      <span
        className="streak-rolling-digit-strip"
        style={{
          transform: `translate3d(0, -${offset * 10}%, 0)`,
          transitionDuration: fast ? '0.4s' : '0.65s',
        }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <span key={n} className="streak-rolling-digit-cell">
            {n}
          </span>
        ))}
      </span>
    </span>
  );
}

/**
 * Odometer-style roll from `from` to `to` (e.g. 2 → 3).
 */
export default function StreakRollingNumber({
  from = 1,
  to = 1,
  fast = false,
  className = '',
  rollDelayMs = 280,
  onCountTick,
}) {
  const fromNum = Number(from);
  const toValue = Math.max(1, Number(to) || 1);
  const fromValue = toValue === 1 && fromNum === 0 ? 0 : Math.max(1, fromNum || 1);

  const columns = useMemo(() => {
    const maxLen = Math.max(String(fromValue).length, String(toValue).length);
    const fromStr = String(fromValue).padStart(maxLen, ' ');
    const toStr = String(toValue).padStart(maxLen, ' ');
    return fromStr.split('').map((fromDigit, i) => ({
      fromDigit,
      toDigit: toStr[i],
      delayMs: rollDelayMs + i * (fast ? 60 : 100),
    }));
  }, [fromValue, toValue, rollDelayMs, fast]);

  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (fromValue === toValue) {
      if (fromValue !== 0) {
        const t = window.setTimeout(() => {
          setPulse(true);
          onCountTick?.();
        }, rollDelayMs);
        const t2 = window.setTimeout(() => setPulse(false), rollDelayMs + 400);
        return () => {
          window.clearTimeout(t);
          window.clearTimeout(t2);
        };
      }
      return undefined;
    }
    const t = window.setTimeout(() => {
      setPulse(true);
      onCountTick?.();
      window.setTimeout(() => setPulse(false), 400);
    }, rollDelayMs + columns.length * (fast ? 60 : 100) + (fast ? 400 : 650));
    return () => window.clearTimeout(t);
  }, [fromValue, toValue, rollDelayMs, fast, columns.length, onCountTick]);

  return (
    <span
      className={`streak-rolling-number${pulse ? ' streak-count-tick' : ''} ${className}`.trim()}
      aria-label={`${toValue} day`}
    >
      {columns.map((col, i) => (
        <RollingDigitColumn
          key={`${col.fromDigit}-${col.toDigit}-${i}`}
          fromDigit={col.fromDigit}
          toDigit={col.toDigit}
          fast={fast}
          delayMs={col.delayMs}
        />
      ))}
    </span>
  );
}
